import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { navigate, paramsExtras, paramsSummary, useToast, useTRPC } from '@barev/web';
import { useEffect, useState } from 'react';
import type { Me } from '../App.tsx';
import { errText, useT } from '../i18n.tsx';
import { Avatar, Dialog, Field, I, PlanBadge, Skeleton } from '../components/kit.tsx';
import { GROUP_DOT, statusLabel } from './Orders.tsx';
import { ParamsForm, SlotPicker, type ParamField } from './NewOrder.tsx';

const MIN = 60_000;

export function OrderCard({ id, me }: { id: string; me: Me }) {
  const { t, shared, sharedT, date, dateTime, time, amd } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const o = useQuery({ ...trpc.bo.orders.get.queryOptions({ id }), refetchInterval: 15_000 });
  const [dlg, setDlg] = useState<'reschedule' | 'cancel' | 'edit' | null>(null);
  const [check, setCheck] = useState<{ code: string; requiresPhoto: boolean } | null>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: trpc.bo.orders.get.queryKey({ id }) });
    qc.invalidateQueries({ queryKey: trpc.bo.orders.candidates.queryKey({ id }) });
    qc.invalidateQueries({ queryKey: trpc.bo.orders.list.queryKey() });
    qc.invalidateQueries({ queryKey: trpc.bo.orders.counts.queryKey() });
    qc.invalidateQueries({ queryKey: trpc.bo.dashboard.attention.queryKey() });
  };
  const onError = (e: unknown) => toast(errText(t, e), 'error');
  const transition = useMutation(trpc.bo.orders.transition.mutationOptions({ onSuccess: () => (refresh(), toast(t('ord.statusChanged'))), onError }));
  const markPaid = useMutation(trpc.bo.orders.markPaid.mutationOptions({ onSuccess: () => (refresh(), toast(t('ord.paidDone'))), onError }));
  const setChecklist = useMutation(trpc.bo.orders.checklist.mutationOptions({ onSuccess: refresh, onError }));
  const chat = useMutation(trpc.bo.chats.openFor.mutationOptions({ onSuccess: (r) => navigate(`/chats/${r.id}`), onError }));
  const canChat = me.roles.some((r) => r === 'SUPERADMIN' || r === 'SUPPORT' || r === 'CONCIERGE');

  const back = (
    <a className="bo-back" href="#/orders">
      <I name="back" size={16} />
      {t('ord.back')}
    </a>
  );
  if (o.isError)
    return (
      <>
        {back}
        <div className="bo-panel bo-empty">{errText(t, o.error)}</div>
      </>
    );
  if (!o.data)
    return (
      <div className="bo-col">
        <Skeleton h={90} />
        <Skeleton h={260} />
      </div>
    );
  const d = o.data;
  const end = new Date(d.scheduledAt.getTime() + d.durationMinutes * MIN);
  const extras = paramsExtras(sharedT, d.params);
  const stepIdx = d.steps.indexOf(d.status as never);

  return (
    <>
      {back}
      <header className="bo-ohead">
        <div style={{ minWidth: 0 }}>
          <div className="bo-ohead__kicker">
            <span className="bo-num">№{d.number}</span>
            <span className="bo-status" style={{ ['--dot' as string]: GROUP_DOT[d.status === 'CANCELLED' ? 'cancelled' : d.group === 'new' && d.pending ? 'pending' : d.group] }}>
              {statusLabel(shared, d.flow, d.status)}
            </span>
          </div>
          <h1 className="bo-title" style={{ fontSize: 'clamp(26px, 3.4vw, 38px)' }}>
            {shared(`services.${d.service}.name`)}
          </h1>
          <div className="bo-muted" style={{ marginTop: 6 }}>
            {dateTime(d.scheduledAt)} – {time(end)}
            {d.service === 'cleaning' && <> · {paramsSummary(sharedT, d.service, d.params)}</>}
          </div>
        </div>
      </header>

      {d.status !== 'CANCELLED' && (
        <ol className="bo-steps" aria-label={t('ord.progress')}>
          {d.steps.map((s, i) => (
            <li key={s} data-state={i < stepIdx ? 'done' : i === stepIdx ? 'now' : 'next'}>
              {statusLabel(shared, d.flow, s)}
            </li>
          ))}
        </ol>
      )}

      <div className="bo-client-grid">
        <div className="bo-col">
          <ProviderPanel order={d} onChanged={refresh} canChat={canChat} />

          <section className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t('ord.details')}</h2>
              {d.status !== 'CANCELLED' && (
                <button type="button" className="bo-btn bo-btn--sm bo-btn--ghost" onClick={() => setDlg('edit')}>
                  {t('ord.editComposition')}
                </button>
              )}
            </div>
            <dl className="bo-kv">
              <dt>{t('ord.when')}</dt>
              <dd>
                {date(d.scheduledAt)}, {time(d.scheduledAt)} – {time(end)}
                <span className="bo-faint"> · {t('ord.minutes', { n: d.durationMinutes })}</span>
              </dd>
              <dt>{t('ord.where')}</dt>
              <dd>{d.isOnline ? shared('common.online') : [d.address, d.district ? shared(`districts.${d.district}`) : null].filter(Boolean).join(', ') || '—'}</dd>
              {d.service === 'cleaning' && (
                <>
                  <dt>{t('ord.params')}</dt>
                  <dd>{paramsSummary(sharedT, d.service, d.params)}</dd>
                </>
              )}
              {d.service !== 'cleaning' &&
                Object.entries(d.params)
                  .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
                  .map(([k, v]) => (
                    <FragmentKV key={k} k={paramLabel(shared, d.service, k)} v={paramValue(shared, d.service, k, v)} />
                  ))}
              {extras.length > 0 && (
                <>
                  <dt>{t('ord.extras')}</dt>
                  <dd>{extras.map((x) => x.label).join(', ')}</dd>
                </>
              )}
              {d.note && (
                <>
                  <dt>{t('ord.note')}</dt>
                  <dd style={{ whiteSpace: 'pre-line' }}>{d.note}</dd>
                </>
              )}
              <dt>{t('ord.lang')}</dt>
              <dd>{d.locale.toUpperCase()}</dd>
              <dt>{t('ord.created')}</dt>
              <dd>{dateTime(d.createdAt)}</dd>
            </dl>
          </section>

          {d.checklist.length > 0 && (
            <section className="bo-panel">
              <div className="bo-panel__head">
                <h2 className="bo-h2">{t('ord.checklist')}</h2>
                <span className="bo-faint">
                  {d.checklist.filter((c) => c.done).length}/{d.checklist.length}
                </span>
              </div>
              <ul className="bo-checks">
                {d.checklist.map((c) => (
                  <li key={c.code} data-done={c.done || undefined}>
                    <button
                      type="button"
                      className="bo-checks__toggle"
                      disabled={setChecklist.isPending || d.status === 'CANCELLED'}
                      title={c.done ? t('ord.uncheck') : t('ord.checkForProvider')}
                      onClick={() => (c.done ? setChecklist.mutate({ id: d.id, code: c.code, checked: false }) : c.requiresPhoto && !c.fileId ? setCheck(c) : setChecklist.mutate({ id: d.id, code: c.code, checked: true }))}
                    >
                      <span className="bo-checks__box">{c.done && <I name="check" size={14} stroke={3} />}</span>
                      {shared(`checklist.${c.code}`)}
                    </button>
                    {c.requiresPhoto && !c.fileId && !c.waived && <span className="bo-faint bo-small"> · {t('ord.needsPhoto')}</span>}
                    {c.waived && <span className="bo-faint bo-small"> · {t('ord.noPhotoReason', { reason: c.waived })}</span>}
                    {c.byStaff && <span className="bo-faint bo-small"> · {t('ord.byStaff')}</span>}
                  </li>
                ))}
              </ul>
              {d.photos.length > 0 && (
                <div className="bo-photos">
                  {d.photos.map((f) => (
                    <a key={f} href={`/files/bo/order-file/${f}`} target="_blank" rel="noreferrer">
                      <img src={`/files/bo/order-file/${f}`} alt={t('ord.photo')} loading="lazy" />
                    </a>
                  ))}
                </div>
              )}
            </section>
          )}

          <section className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t('ord.history')}</h2>
            </div>
            <ol className="bo-timeline">
              {d.history.map((h) => (
                <li key={h.id}>
                  <span className="bo-feed__time">{dateTime(h.at)}</span>
                  <span>
                    <b>{historyTitle(t, shared, d.flow, h)}</b>
                    <span className="bo-muted">
                      {' '}
                      · {h.actorName ?? shared(`admin.actors.${h.actor}`)}
                      {h.comment && ` · ${h.comment}`}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <div className="bo-col">
          <section className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t('ord.client')}</h2>
            </div>
            <a href={`#/clients/${d.client.id}`} className="bo-person">
              <Avatar name={d.client.name} id={d.client.id} size={40} />
              <span style={{ minWidth: 0 }}>
                <b>{d.client.name}</b>
                <span className="bo-small bo-muted" style={{ display: 'block' }}>
                  {[d.client.username && `@${d.client.username}`, d.client.phone].filter(Boolean).join(' · ') || '—'}
                </span>
              </span>
              <PlanBadge plan={d.client.plan} t={t} />
            </a>
            {canChat && (
              <button type="button" className="bo-btn bo-btn--ghost bo-btn--sm" style={{ marginTop: 12 }} onClick={() => chat.mutate({ clientId: d.client.id, channel: 'SUPPORT' })}>
                <I name="chats" size={16} />
                {t('ord.writeClient')}
              </button>
            )}
          </section>

          {d.money && (
            <section className="bo-panel bo-money-card">
              <span className="bo-label" style={{ color: 'inherit', opacity: 0.75 }}>
                {t('ord.money')}
              </span>
              <div className="bo-bignum" style={{ margin: '6px 0 10px' }}>
                {amd(d.money.total)}
              </div>
              <dl className="bo-kv bo-kv--plain">
                <dt>{t('ord.base')}</dt>
                <dd className="bo-num">{amd(d.money.base)}</dd>
                {d.money.discount > 0 && (
                  <>
                    <dt>{t('ord.discount', { pct: d.money.discountPct })}</dt>
                    <dd className="bo-num">−{amd(d.money.discount)}</dd>
                  </>
                )}
                {d.money.payout != null && (
                  <>
                    <dt>{t('ord.payout')}</dt>
                    <dd className="bo-num">{amd(d.money.payout)}</dd>
                  </>
                )}
                {d.money.cancelFee > 0 && (
                  <>
                    <dt>{t('ord.cancelFee')}</dt>
                    <dd className="bo-num">{amd(d.money.cancelFee)}</dd>
                  </>
                )}
              </dl>
            </section>
          )}

          <section className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t('ord.payment')}</h2>
              <span className={`bo-chip${d.paymentStatus === 'PAID' ? ' bo-chip--ok' : ''}`}>{shared(`payment.${d.paymentStatus}`)}</span>
            </div>
            {d.paidAt && (
              <p className="bo-hint">
                {dateTime(d.paidAt)}
                {d.paymentMethod && ` · ${d.paymentMethod.ru}`}
              </p>
            )}
            {d.paymentStatus === 'UNPAID' && d.status !== 'CANCELLED' && (
              <button type="button" className="bo-btn bo-btn--sm" onClick={() => confirm(t('ord.confirmPaid')) && markPaid.mutate({ id })} disabled={markPaid.isPending}>
                <I name="check" size={16} />
                {t('ord.markPaid')}
              </button>
            )}
          </section>

          {(d.adminTransitions.length > 0 || d.canReschedule || d.canCancel) && (
            <section className="bo-panel">
              <div className="bo-panel__head">
                <h2 className="bo-h2">{t('ord.actions')}</h2>
              </div>
              <div className="bo-col" style={{ gap: 8 }}>
                {d.adminTransitions.map((to) => (
                  <button
                    key={to}
                    type="button"
                    className="bo-btn bo-btn--ghost bo-btn--sm"
                    disabled={transition.isPending}
                    onClick={() => confirm(t('ord.confirmStatus', { status: statusLabel(shared, d.flow, to) })) && transition.mutate({ id, to })}
                  >
                    {t('ord.setStatus', { status: statusLabel(shared, d.flow, to) })}
                  </button>
                ))}
                {d.canReschedule && (
                  <button type="button" className="bo-btn bo-btn--ghost bo-btn--sm" onClick={() => setDlg('reschedule')}>
                    {t('ord.reschedule')}
                  </button>
                )}
                {d.canCancel && (
                  <button type="button" className="bo-btn bo-btn--danger bo-btn--sm" onClick={() => setDlg('cancel')}>
                    {t('ord.cancel')}
                  </button>
                )}
              </div>
            </section>
          )}

          {d.rating && (
            <section className="bo-panel">
              <div className="bo-panel__head">
                <h2 className="bo-h2">{t('ord.rating')}</h2>
                <span className="bo-stars">{'★'.repeat(d.rating.score)}{'☆'.repeat(5 - d.rating.score)}</span>
              </div>
              {d.rating.comment ? <p style={{ margin: 0 }}>{d.rating.comment}</p> : <p className="bo-hint">{t('ord.noComment')}</p>}
            </section>
          )}

          {d.offers.length > 0 && (
            <section className="bo-panel">
              <div className="bo-panel__head">
                <h2 className="bo-h2">{t('ord.offers')}</h2>
              </div>
              <ul className="bo-list">
                {d.offers.map((x) => (
                  <li key={x.id}>
                    <span className="bo-grow">
                      <b>{x.name}</b>
                      <span className="bo-small bo-muted" style={{ display: 'block' }}>
                        {dateTime(x.createdAt)} · {x.by ?? t('ord.bySystem')}
                      </span>
                    </span>
                    <span className="bo-chip">{shared(`admin.offerStatus.${x.status}`)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {d.cancelReason && (
            <section className="bo-panel">
              <div className="bo-panel__head">
                <h2 className="bo-h2">{t('ord.cancelReason')}</h2>
              </div>
              <p style={{ margin: 0 }}>{d.cancelReason}</p>
            </section>
          )}
        </div>
      </div>

      {dlg === 'reschedule' && <RescheduleDialog order={d} onClose={() => setDlg(null)} onDone={refresh} />}
      {dlg === 'edit' && <EditDialog order={d} onClose={() => setDlg(null)} onDone={refresh} />}
      {check && <ChecklistPhotoDialog orderId={d.id} code={check.code} onClose={() => setCheck(null)} onDone={refresh} />}
      {dlg === 'cancel' && <CancelDialog id={id} showFee={!!d.money} onClose={() => setDlg(null)} onDone={refresh} />}
    </>
  );
}

function FragmentKV({ k, v }: { k: string; v: string }) {
  return (
    <>
      <dt>{k}</dt>
      <dd>{v}</dd>
    </>
  );
}
const paramLabel = (shared: (k: string) => string, svc: string, key: string) => {
  const k = `params.${svc}.${key}.label`;
  const v = shared(k);
  return v === k ? key : v;
};
const paramValue = (shared: (k: string) => string, svc: string, key: string, value: unknown) => {
  const k = `params.${svc}.${key}.${String(value)}`;
  const v = shared(k);
  return v === k ? String(value) : v;
};

type HistoryRow = { event: string | null; from: string | null; to: string; comment: string | null };
function historyTitle(t: ReturnType<typeof useT>['t'], shared: (k: string) => string, flow: string, h: HistoryRow) {
  if (h.event) {
    const own = `ord.ev.${h.event}`;
    const ownTr = t(own);
    if (ownTr !== own) return ownTr;
    const sh = `admin.events.${h.event}`;
    const shTr = shared(sh);
    if (shTr !== sh) return shTr;
  }
  if (!h.from) return t('ord.ev.created');
  return statusLabel(shared, flow, h.to);
}

// ───── исполнитель и подбор ─────

type OrderData = NonNullable<ReturnType<typeof useOrderData>>;
function useOrderData(id: string) {
  const trpc = useTRPC();
  return useQuery(trpc.bo.orders.get.queryOptions({ id })).data;
}

function ProviderPanel({ order: d, onChanged, canChat }: { order: OrderData; onChanged: () => void; canChat: boolean }) {
  const { t, shared, amd } = useT();
  const trpc = useTRPC();
  const toast = useToast();
  const [showBusy, setShowBusy] = useState(false);
  const onError = (e: unknown) => toast(errText(t, e), 'error');
  const cands = useQuery({ ...trpc.bo.orders.candidates.queryOptions({ id: d.id }), enabled: d.canAssign && !d.pending });
  const assign = useMutation(trpc.bo.orders.assign.mutationOptions({ onSuccess: () => (onChanged(), toast(t('ord.offered'))), onError }));
  const auto = useMutation(
    trpc.bo.orders.auto.mutationOptions({
      onSuccess: (r) => {
        onChanged();
        toast(r.result === 'offered' ? t('ord.autoOffered', { name: r.target }) : t('ord.autoNone'), r.result === 'offered' ? undefined : 'error');
      },
      onError,
    }),
  );
  const unassign = useMutation(trpc.bo.orders.unassign.mutationOptions({ onSuccess: () => (onChanged(), toast(t('ord.unassigned'))), onError }));
  const chat = useMutation(trpc.bo.chats.openFor.mutationOptions({ onSuccess: (r) => navigate(`/chats/${r.id}`), onError }));
  const [left, setLeft] = useState('');
  useEffect(() => {
    const exp = d.pending?.expiresAt;
    if (!exp) return setLeft('');
    const tick = () => setLeft(t('ord.minLeft', { n: Math.max(0, Math.ceil((exp.getTime() - Date.now()) / MIN)) }));
    tick();
    const iv = setInterval(tick, 15_000);
    return () => clearInterval(iv);
  }, [d.pending?.expiresAt, t]);

  return (
    <section className="bo-panel">
      <div className="bo-panel__head">
        <h2 className="bo-h2">{t('ord.provider')}</h2>
        {d.canUnassign && (
          <button type="button" className="bo-btn bo-btn--ghost bo-btn--sm" disabled={unassign.isPending} onClick={() => confirm(d.provider ? t('ord.confirmUnassign') : t('ord.confirmRevoke')) && unassign.mutate({ id: d.id })}>
            {d.provider ? t('ord.unassign') : t('ord.revoke')}
          </button>
        )}
      </div>

      {d.provider ? (
        <>
        <div className="bo-person">
          <Avatar name={d.provider.name} id={d.provider.id} size={40} />
          <a href={`#/providers/${d.provider.id}`} style={{ minWidth: 0, color: 'inherit', textDecoration: 'none' }}>
            <b>{d.provider.name}</b>
            <span className="bo-small bo-muted" style={{ display: 'block' }}>
              {[d.company?.name, d.provider.phone, d.provider.username && `@${d.provider.username}`].filter(Boolean).join(' · ') || '—'}
            </span>
          </a>
        </div>
        <ContactRow providerId={d.provider.id} phone={d.provider.phone} canChat={canChat} onChat={(id) => chat.mutate({ providerId: id })} busy={chat.isPending} />
        </>
      ) : d.pending ? (
        <>
        <div className="bo-pendingbox">
          {d.pending.auto ? <I name="bot" size={20} /> : <I name="send" size={20} />}
          <span className="bo-grow">
            <b>{d.pending.company ? t('ord.pendingCompany', { name: d.pending.name }) : t('ord.pendingProvider', { name: d.pending.name })}</b>
            <span className="bo-small" style={{ display: 'block', opacity: 0.8 }}>
              {[d.pending.auto ? t('ord.byAuto') : null, left].filter(Boolean).join(' · ')}
            </span>
          </span>
        </div>
        {d.pending.providerId && <ContactRow providerId={d.pending.providerId} phone={d.pending.phone} canChat={canChat} onChat={(id) => chat.mutate({ providerId: id })} busy={chat.isPending} />}
        </>
      ) : d.canAssign ? (
        <>
          <div className="bo-assignbar">
            <p className="bo-hint bo-grow" style={{ margin: 0 }}>
              {t('ord.pickHint')}
            </p>
            <button type="button" className="bo-btn bo-btn--sm" onClick={() => auto.mutate({ id: d.id })} disabled={auto.isPending}>
              <I name="bot" size={16} />
              {t('ord.autoPick')}
            </button>
          </div>
          {!cands.data ? (
            <Skeleton h={120} />
          ) : cands.data.free.length === 0 ? (
            <p className="bo-empty" style={{ padding: '16px 0' }}>
              {t('ord.noFree')}
            </p>
          ) : (
            <ul className="bo-cands">
              {cands.data.free.map((c, i) => (
                <li key={`${c.kind}:${c.id}`} data-best={c.best || undefined}>
                  <span className="bo-cands__rank">{i + 1}</span>
                  <span className="bo-grow" style={{ minWidth: 0 }}>
                    <b>{c.name}</b>
                    {c.kind === 'company' && <span className="bo-chip" style={{ marginLeft: 6 }}>{t('ord.partner')}</span>}
                    {c.best && <span className="bo-chip bo-chip--ok" style={{ marginLeft: 6 }}>{t('ord.best')}</span>}
                    {c.tried && <span className="bo-chip bo-chip--warn" style={{ marginLeft: 6 }}>{t('ord.tried')}</span>}
                    <span className="bo-small bo-muted" style={{ display: 'block' }}>
                      {[
                        c.rating != null ? `★ ${c.rating}` : null,
                        c.languages.join(', ').toUpperCase(),
                        ...c.reasons.map((r) => shared(`admin.reasons.${r}`)),
                        c.kind === 'company' && c.partnerFree != null ? t('ord.partnerFree', { n: c.partnerFree, of: c.partnerStaff ?? 0 }) : t('ord.load', { n: c.load }),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    {c.margin != null && (
                      <span className="bo-small" style={{ display: 'block' }}>
                        {t('ord.candMoney', { cost: amd(c.cost ?? 0), margin: amd(c.margin) })}
                      </span>
                    )}
                  </span>
                  <button type="button" className="bo-btn bo-btn--sm" disabled={assign.isPending} onClick={() => assign.mutate({ id: d.id, target: { kind: c.kind, id: c.id } })}>
                    {t('ord.offer')}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!!cands.data?.busy.length && (
            <>
              <button type="button" className="bo-side__link" style={{ color: 'var(--bc-ink-2)', marginTop: 10 }} onClick={() => setShowBusy((s) => !s)}>
                {t('ord.busy', { n: cands.data.busy.length })} {showBusy ? '▴' : '▾'}
              </button>
              {showBusy && <p className="bo-hint" style={{ marginTop: 6 }}>{cands.data.busy.map((c) => c.name).join(', ')}</p>}
            </>
          )}
        </>
      ) : (
        <p className="bo-hint">{t('ord.noProvider')}</p>
      )}
    </section>
  );
}

/** Связаться с исполнителем: чат в рабочем боте и звонок */
function ContactRow({ providerId, phone, canChat, onChat, busy }: { providerId: string; phone: string | null; canChat: boolean; onChat: (id: string) => void; busy: boolean }) {
  const { t } = useT();
  return (
    <div className="bo-contact">
      {canChat && (
        <button type="button" className="bo-btn bo-btn--sm" onClick={() => onChat(providerId)} disabled={busy}>
          <I name="chats" size={16} />
          {t('ord.writeProvider')}
        </button>
      )}
      {phone ? (
        <a className="bo-btn bo-btn--ghost bo-btn--sm" href={`tel:${phone.replace(/[^\d+]/g, '')}`}>
          <I name="phone" size={16} />
          {t('ord.call')}
        </a>
      ) : (
        <span className="bo-btn bo-btn--ghost bo-btn--sm" aria-disabled="true" title={t('ord.noPhone')} style={{ opacity: 0.45, cursor: 'default' }}>
          <I name="phone" size={16} />
          {t('ord.call')}
        </span>
      )}
    </div>
  );
}

// ───── диалоги ─────

function RescheduleDialog({ order: d, onClose, onDone }: { order: OrderData; onClose: () => void; onDone: () => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const toast = useToast();
  const [at, setAt] = useState<{ when: Date; busy: boolean } | null>(null);
  const [comment, setComment] = useState('');
  const save = useMutation(
    trpc.bo.orders.reschedule.mutationOptions({
      onSuccess: (r) => {
        onDone();
        toast(r.keptProvider ? t('ord.rescheduled') : t('ord.rescheduledFree'));
        onClose();
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('ord.rescheduleTitle', { n: d.number })}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button type="button" className="bo-btn" disabled={!at || save.isPending} onClick={() => at && save.mutate({ id: d.id, scheduledAt: at.when, allowBusySlot: at.busy, comment: comment || undefined })}>
            {t('ord.reschedule')}
          </button>
        </>
      }
    >
      <SlotPicker service={d.service} params={d.params} excludeOrderId={d.id} initial={d.scheduledAt} onChange={setAt} />
      <Field label={t('ord.comment')}>
        <input className="bo-input" value={comment} maxLength={500} onChange={(e) => setComment(e.target.value)} placeholder={t('ord.commentPh')} />
      </Field>
      {d.provider && <p className="bo-hint">{t('ord.rescheduleHint')}</p>}
    </Dialog>
  );
}

function CancelDialog({ id, showFee, onClose, onDone }: { id: string; showFee: boolean; onClose: () => void; onDone: () => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [fee, setFee] = useState('');
  const cancel = useMutation(
    trpc.bo.orders.cancel.mutationOptions({
      onSuccess: () => {
        onDone();
        toast(t('ord.cancelled'));
        onClose();
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('ord.cancelTitle')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button type="button" className="bo-btn bo-btn--accent" disabled={!reason.trim() || cancel.isPending} onClick={() => cancel.mutate({ id, reason, fee: Number(fee) || 0 })}>
            {t('ord.cancel')}
          </button>
        </>
      }
    >
      <Field label={t('ord.cancelReason')}>
        <textarea className="bo-textarea" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} placeholder={t('ord.cancelReasonPh')} />
      </Field>
      {showFee && (
        <Field label={t('ord.cancelFee')} hint={t('ord.cancelFeeHint')}>
          <input className="bo-input" inputMode="numeric" value={fee} onChange={(e) => setFee(e.target.value.replace(/\D/g, ''))} placeholder="0" />
        </Field>
      )}
      <p className="bo-hint">{t('ord.cancelHint')}</p>
    </Dialog>
  );
}

/** Изменить состав заказа: пересчёт по прайсу с прежней скидкой или своя цена с причиной */
function EditDialog({ order, onClose, onDone }: { order: { id: string; number: number; service: string; params: Record<string, unknown> }; onClose: () => void; onDone: () => void }) {
  const { t, amd } = useT();
  const trpc = useTRPC();
  const toast = useToast();
  const services = useQuery(trpc.bo.orders.services.queryOptions());
  const fields = (services.data?.find((s) => s.code === order.service)?.paramsSchema.fields ?? []) as ParamField[];
  const [params, setParams] = useState<Record<string, unknown>>(() => ({ ...order.params }));
  const [price, setPrice] = useState('');
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(true);
  const p = useQuery({ ...trpc.bo.orders.editPreview.queryOptions({ id: order.id, params }), placeholderData: (prev) => prev });
  const manual = price !== '' && p.data && Number(price) !== p.data.totalPrice;
  const save = useMutation(
    trpc.bo.orders.edit.mutationOptions({
      onSuccess: (r) => {
        toast(r.providerBusy ? t('ord.editedBusy') : t('ord.edited'));
        onDone();
        onClose();
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={t('ord.editTitle', { n: order.number })}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button type="button" className="bo-btn" disabled={!p.data || save.isPending || (!!manual && !reason.trim())} onClick={() => save.mutate({ id: order.id, params, totalPrice: price === '' ? null : Number(price), reason: reason.trim() || null, notify })}>
            {t('ui.save')}
          </button>
        </>
      }
    >
      {fields.length > 0 && <ParamsForm service={order.service} fields={fields} value={params} onChange={setParams} />}
      {p.isError && <p className="bo-hint" style={{ color: 'var(--bc-danger)' }}>{errText(t, p.error)}</p>}
      {p.data && (
        <div className="bo-side-panel__note" style={{ margin: '14px 0' }}>
          {t('ord.editPrice', { was: amd(p.data.was.totalPrice), now: amd(p.data.totalPrice), pct: p.data.discountPct })}
          <br />
          {t('ord.editDuration', { was: p.data.was.durationMinutes, now: p.data.durationMinutes })}
          {p.data.providerBusy && (
            <>
              <br />⚠️ {t('ord.editBusy')}
            </>
          )}
          {p.data.paid && (
            <>
              <br />💳 {t('ord.editPaid')}
            </>
          )}
        </div>
      )}
      <div className="bo-row2">
        <Field label={t('ord.editOwnPrice')} hint={p.data ? t('ord.editOwnPriceHint', { n: amd(p.data.totalPrice) }) : undefined}>
          <input className="bo-input" inputMode="numeric" value={price} placeholder={p.data ? String(p.data.totalPrice) : ''} onChange={(e) => setPrice(e.target.value.replace(/\D/g, '').slice(0, 8))} />
        </Field>
        <Field label={manual ? t('ord.editReasonRequired') : t('ord.editReason')}>
          <input className="bo-input" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} placeholder={t('ord.editReasonPh')} />
        </Field>
      </div>
      <label className="bo-check">
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
        {t('ord.editNotify')}
      </label>
    </Dialog>
  );
}

/** Пункт с фото: загрузить фото или отметить без фото с причиной */
function ChecklistPhotoDialog({ orderId, code, onClose, onDone }: { orderId: string; code: string; onClose: () => void; onDone: () => void }) {
  const { t, shared } = useT();
  const trpc = useTRPC();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const mark = useMutation(trpc.bo.orders.checklist.mutationOptions({ onSuccess: () => (onDone(), onClose()), onError: (e) => toast(errText(t, e), 'error') }));
  const upload = async (file: File) => {
    setBusy(true);
    const fd = new FormData();
    fd.append('code', code);
    fd.append('file', file);
    const res = await fetch(`/files/bo/checklist-photo/${orderId}`, { method: 'POST', body: fd, credentials: 'include' }).catch(() => null);
    setBusy(false);
    if (!res?.ok) {
      const err = (await res?.json().catch(() => null)) as { error?: string } | null;
      toast(errText(t, { message: err?.error ?? 'NETWORK' }), 'error');
      return;
    }
    onDone();
    onClose();
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={shared(`checklist.${code}`)}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button type="button" className="bo-btn" disabled={!reason.trim() || mark.isPending} onClick={() => mark.mutate({ id: orderId, code, checked: true, reason })}>
            {t('ord.checkNoPhoto')}
          </button>
        </>
      }
    >
      <p className="bo-hint" style={{ marginTop: 0 }}>
        {t('ord.photoHint')}
      </p>
      <label className="bo-btn bo-btn--accent" style={{ display: 'inline-flex', marginBottom: 14, opacity: busy ? 0.6 : 1 }}>
        <I name="clip" size={16} />
        {busy ? t('ui.loading') : t('ord.uploadPhoto')}
        <input type="file" accept="image/*" hidden disabled={busy} onChange={(e) => e.target.files?.[0] && void upload(e.target.files[0])} />
      </label>
      <Field label={t('ord.noPhotoField')}>
        <input className="bo-input" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} placeholder={t('ord.noPhotoPh')} />
      </Field>
    </Dialog>
  );
}
